import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Wand2, Apple, BookOpen, Star } from "lucide-react";

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 w-9 h-9 rounded-full bg-amber-400/15 border border-amber-400/30 flex items-center justify-center shrink-0">
      <Icon size={16} className="text-amber-300" />
    </div>
    <div className="flex-1">
      <div className="text-xs uppercase tracking-[0.2em] text-amber-300/80 mb-0.5">{label}</div>
      <div className="text-emerald-50/90 leading-relaxed">{children}</div>
    </div>
  </div>
);

const CharacterLoreModal = ({ character, open, onClose }) => {
  return (
    <AnimatePresence>
      {open && character && (
        <>
          {/* Overlay */}
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] bg-[#03080A]/85 backdrop-blur-md"
            onClick={onClose}
            data-testid="lore-overlay"
          />

          {/* Modal */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[81] flex items-center justify-center p-4 pointer-events-none"
            data-testid={`lore-modal-${character.id}`}
          >
            <div
              className="pointer-events-auto relative w-full max-w-4xl max-h-[92vh] overflow-hidden glass-card !p-0 grid md:grid-cols-2"
              role="dialog"
              aria-modal="true"
            >
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-emerald-950/70 border border-amber-500/30 hover:border-amber-400/70 text-amber-200 flex items-center justify-center transition"
                aria-label="Close"
                data-testid="lore-close"
              >
                <X size={18} />
              </button>

              {/* Portrait */}
              <div className="relative bg-emerald-950/30 overflow-hidden min-h-[300px] md:min-h-[560px]">
                <img
                  src={character.image}
                  alt={character.name}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08140B] via-transparent to-transparent" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,215,0,0.18),transparent_70%)]" />
                <div className="absolute bottom-5 left-5 right-5">
                  <span className="font-accent text-amber-300 text-base md:text-lg">
                    {character.title}
                  </span>
                </div>
              </div>

              {/* Details */}
              <div className="p-7 md:p-9 overflow-y-auto max-h-[92vh]">
                <Sparkles className="text-amber-300 mb-3" size={24} />
                <h3 className="font-heading text-4xl md:text-5xl text-white leading-tight">
                  {character.name}
                </h3>
                <p className="mt-3 text-emerald-50/80 leading-relaxed text-lg italic">
                  {character.blurb}
                </p>
                <div className="section-divider !mx-0 my-7" />

                <div className="space-y-5">
                  <Row icon={Wand2} label="Special skill">{character.skill}</Row>
                  <Row icon={Apple} label="Favourite snack">{character.food}</Row>
                  <Row icon={BookOpen} label="Appears in">
                    <ul className="space-y-1 mt-1">
                      {character.appears_in.map((b) => (
                        <li key={b} className="flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-amber-400" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </Row>
                  <Row icon={Star} label="Fun fact">{character.fun_fact}</Row>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CharacterLoreModal;
